import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import atoms from '@Components/Utilities/SandhillsAtoms';
import EmptyLegAlertModal from '@Components/Shared/Modals/EmptyLegAlertModal';
import Modal from '@Components/Shared/Modals/Modal';

const buildRouteRows = (routeAlerts) => {
    if (!routeAlerts?.length) {
        return [];
    }

    return routeAlerts.map((alert, index) => ({
        id: alert.EmptyLegAlertID || index + 1,
        from: alert.DepartureCityDisplay || alert.DepartureCity || '—',
        to: alert.ArrivalCityDisplay || alert.ArrivalCity || '—'
    }));
};

const buildCityRows = (cityAlerts) => {
    if (!cityAlerts?.length) {
        return [];
    }

    return cityAlerts.map((alert, index) => ({
        id: alert.EmptyLegAlertID || index + 1,
        city: alert.DepartureCityDisplay || alert.DepartureCity || '—'
    }));
};

const compareAlphabetically = (leftValue, rightValue, direction = 'asc') => {
    const left = (leftValue || '').toString();
    const right = (rightValue || '').toString();
    const sortOrder = left.localeCompare(right, undefined, { sensitivity: 'base' });

    return direction === 'desc' ? sortOrder * -1 : sortOrder;
};

const EmptyLegAlertsAccountView = (props) => {
    const routesConfig = useRecoilValue(atoms.routes);

    const [showAlertModal, setShowAlertModal] = useState(false);
    const [routeRows, setRouteRows] = useState(() => buildRouteRows(props.RouteAlerts));
    const [cityRows, setCityRows] = useState(() => buildCityRows(props.CityAlerts));
    const [routeSort, setRouteSort] = useState({ field: 'from', direction: 'asc' });
    const [citySortDirection, setCitySortDirection] = useState('asc');
    const [selectedRouteIds, setSelectedRouteIds] = useState([]);
    const [selectedCityIds, setSelectedCityIds] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    const addAlert = () => {
        setShowAlertModal(true);
    };

    const refreshAlerts = () => {
        return sandhillsRequest({
            url: routesConfig.EmptyLegAlertList,
            method: 'get',
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response?.success) {
                setRouteRows(buildRouteRows(response.RouteAlerts));
                setCityRows(buildCityRows(response.CityAlerts));
            }
        });
    };

    const handleAlertSaved = () => {
        // The save can add/update multiple route and city alerts at once, so
        // refetch the full alert list from the server rather than trying to
        // reconstruct it from the save response.
        refreshAlerts();
    };

    const toggleRouteSelection = (routeId) => {
        setSelectedRouteIds(previousState => previousState.includes(routeId)
            ? previousState.filter(id => id !== routeId)
            : [...previousState, routeId]);
    };

    const toggleCitySelection = (cityId) => {
        setSelectedCityIds(previousState => previousState.includes(cityId)
            ? previousState.filter(id => id !== cityId)
            : [...previousState, cityId]);
    };

    const clearSelected = () => {
        setSelectedRouteIds([]);
        setSelectedCityIds([]);
    };

    const openDeleteConfirmation = (type, id) => {
        setDeleteError('');
        setDeleteTarget({ type, id });
    };

    const closeDeleteConfirmation = () => {
        setDeleteTarget(null);
    };

    const deleteAlertById = (alertId) => {
        return sandhillsRequest({
            url: routesConfig.EmptyLegAlertDelete.replace('{0}', alertId),
            method: 'post',
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }

        let idsToDelete = [];

        if (deleteTarget.type === 'route' || deleteTarget.type === 'city') {
            idsToDelete = [deleteTarget.id];
        } else if (deleteTarget.type === 'bulk') {
            idsToDelete = [...new Set([...selectedRouteIds, ...selectedCityIds])];
        }

        if (!idsToDelete.length) {
            closeDeleteConfirmation();
            return;
        }

        try {
            const responses = await Promise.all(idsToDelete.map(id => deleteAlertById(id)));
            const failedDelete = responses.find(response => !response?.success);

            if (failedDelete) {
                setDeleteError(failedDelete.message || 'An error occurred while deleting the alert.');
                return;
            }

            if (deleteTarget.type === 'route') {
                setRouteRows(previousState => previousState.filter(route => route.id !== deleteTarget.id));
                setSelectedRouteIds(previousState => previousState.filter(id => id !== deleteTarget.id));
            } else if (deleteTarget.type === 'city') {
                setCityRows(previousState => previousState.filter(city => city.id !== deleteTarget.id));
                setSelectedCityIds(previousState => previousState.filter(id => id !== deleteTarget.id));
            } else if (deleteTarget.type === 'bulk') {
                setRouteRows(previousState => previousState.filter(route => !selectedRouteIds.includes(route.id)));
                setCityRows(previousState => previousState.filter(city => !selectedCityIds.includes(city.id)));
                clearSelected();
            }

            closeDeleteConfirmation();
        } catch {
            setDeleteError('An error occurred while deleting the alert.');
        }
    };

    const toggleRouteSort = (field) => {
        setRouteSort(previousState => ({
            field,
            direction: previousState.field === field && previousState.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const toggleCitySort = () => {
        setCitySortDirection(previousState => previousState === 'asc' ? 'desc' : 'asc');
    };

    const selectedCount = selectedRouteIds.length + selectedCityIds.length;
    const routes = useMemo(() => [...routeRows].sort((left, right) => {
        const primarySort = compareAlphabetically(left[routeSort.field], right[routeSort.field], routeSort.direction);

        if (primarySort !== 0) {
            return primarySort;
        }

        const secondaryField = routeSort.field === 'from' ? 'to' : 'from';
        return compareAlphabetically(left[secondaryField], right[secondaryField], routeSort.direction);
    }), [routeRows, routeSort]);
    const cities = useMemo(() => [...cityRows].sort((left, right) => compareAlphabetically(left.city, right.city, citySortDirection)), [cityRows, citySortDirection]);

    return (
        <div className='account-right'>
            <div className='page-title'>
                <h2>Empty Leg Alerts</h2>
                <div className='btn-container'>
                    <button type='button' className='ts-button alert-button' onClick={addAlert}>
                        <i className='fa-solid fa-plus'></i><span>Add An Alert</span>
                    </button>
                </div>
            </div>

            <h4>Routes</h4>
            <table className='table' id='route-table'>
                <tbody>
                    <tr>
                        <td className='table-check'>&nbsp;</td>
                        <th
                            className={routeSort.field === 'from' ? 'sort-active' : ''}
                            role='button'
                            tabIndex='0'
                            aria-sort={routeSort.field === 'from' ? (routeSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                            onClick={() => toggleRouteSort('from')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRouteSort('from'); } }}
                        >
                            From {routeSort.field === 'from' ? (routeSort.direction === 'asc' ? '↑' : '↓') : ''}
                        </th>
                        <th
                            className={routeSort.field === 'to' ? 'sort-active' : ''}
                            role='button'
                            tabIndex='0'
                            aria-sort={routeSort.field === 'to' ? (routeSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                            onClick={() => toggleRouteSort('to')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRouteSort('to'); } }}
                        >
                            To {routeSort.field === 'to' ? (routeSort.direction === 'asc' ? '↑' : '↓') : ''}
                        </th>
                        <td className='table-buttons'>&nbsp;</td>
                    </tr>
                    {routes.map((route, index) => {
                        const checkboxId = `route-cbx-${route.id ?? index + 1}`;
                        return (
                            <tr key={checkboxId}>
                                <td className='table-check'>
                                    <input
                                        type='checkbox'
                                        id={checkboxId}
                                        checked={selectedRouteIds.includes(route.id)}
                                        onChange={() => toggleRouteSelection(route.id)}
                                    />
                                    <label htmlFor={checkboxId} className='check'>
                                    </label>
                                </td>
                                <td>
                                    <div className='column-content'>
                                        {route.from}
                                    </div>
                                </td>
                                <td>
                                    <div className='column-content'>
                                        {route.to}
                                    </div>
                                </td>
                                <td className='table-buttons'>
                                    <button type='button' className='delete-btn' title='Delete Alert' onClick={() => openDeleteConfirmation('route', route.id)}>
                                        <i className='fa-solid fa-trash'></i>
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <h4>Cities</h4>
            <table className='table' id='city-table'>
                <tbody>
                    <tr>
                        <td className='table-check'>&nbsp;</td>
                        <th
                            className='sort-active'
                            role='button'
                            tabIndex='0'
                            aria-sort={citySortDirection === 'asc' ? 'ascending' : 'descending'}
                            onClick={toggleCitySort}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCitySort(); } }}
                        >
                            City, State {citySortDirection === 'asc' ? '↑' : '↓'}
                        </th>
                    </tr>
                    {cities.map((city, index) => {
                        const checkboxId = `city-cbx-${city.id ?? index + 1}`;
                        return (
                            <tr key={checkboxId}>
                                <td className='table-check'>
                                    <input
                                        type='checkbox'
                                        id={checkboxId}
                                        checked={selectedCityIds.includes(city.id)}
                                        onChange={() => toggleCitySelection(city.id)}
                                    />
                                    <label htmlFor={checkboxId} className='check'>
                                    </label>
                                </td>
                                <td>
                                    <div className='column-content'>
                                        {city.city}
                                    </div>
                                </td>
                                <td className='table-buttons'>
                                    <button type='button' className='delete-btn' title='Delete Alert' onClick={() => openDeleteConfirmation('city', city.id)}>
                                        <i className='fa-solid fa-trash'></i>
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {selectedCount > 0 && (
                <div className="table-actions">
                    <div className='table-action-btns'>
                        <button type='button' className='clear-btn' onClick={clearSelected}>
                            <i className='far fa-times-circle'></i>
                            <div><span className='check-counter'>{selectedCount}</span> Selected</div>
                        </button>
                        <button type='button' className='delete-btn' onClick={() => openDeleteConfirmation('bulk')}>
                            <i className='far fa-trash-alt'></i><span>Delete</span>
                        </button>
                    </div>
                </div>
            )}

            {showAlertModal && (
                <EmptyLegAlertModal
                    onClose={() => setShowAlertModal(false)}
                    onSave={handleAlertSaved}
                    isAccountView={true}
                />
            )}

            {deleteTarget && (
                <Modal containerClassName='delete-modal-content'>
                    <div className='modal-head'>
                        <h2>Delete Alert</h2>
                        <span className='close' role='button' tabIndex='0' onClick={closeDeleteConfirmation} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { closeDeleteConfirmation(); } }}>
                            <i className='fas fa-times'></i>
                        </span>
                    </div>
                    <div className='modal-main'>
                        <p>
                            {deleteTarget.type === 'bulk'
                                ? `Are you sure you wish to delete ${selectedCount} selected alert(s)? This action cannot be undone.`
                                : 'Are you sure you wish to delete the selected alert? This action cannot be undone.'}
                        </p>
                        {deleteError && <div className='error'>{deleteError}</div>}
                    </div>
                    <div className='modal-buttons'>
                        <button type='button' className='save-modal' onClick={confirmDelete}>{deleteTarget.type === 'bulk' ? 'Delete Selected' : 'Delete'}</button>
                        <button type='button' className='close-modal' onClick={closeDeleteConfirmation}>Cancel</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default EmptyLegAlertsAccountView;
